<script setup>
import { useForm, Link } from '@inertiajs/vue3'
import { computed, watch } from 'vue'
import AppLayout from '@/Layouts/AppLayout.vue'
import PageShell from '@/Components/UI/PageShell.vue'
import StickyActionBar from '@/Components/UI/StickyActionBar.vue'
import UiButton from '@/Components/UI/UiButton.vue'
import Icon from '@/Components/UI/Icon.vue'
import { useCurrency } from '@/composables/useCurrency.js'

defineOptions({ layout: AppLayout })

const props = defineProps({
  categories:     { type: Array,  required: true },
  unpaid_rentals: { type: Array,  required: true },
  vehicles:       { type: Array,  required: true },
  errors:         { type: Object, default: () => ({}) },
})

const { setting } = useCurrency()

const today = new Date().toISOString().slice(0, 10)

const form = useForm({
  category:     'unpaid_rental',
  amount_cents: 0,
  description:  '',
  occurred_on:  today,
  rental_id:    null,
  vehicle_id:   null,
})

const categoryLabels = {
  unpaid_rental: 'Unpaid Rental',
  accident:      'Accident',
  damage:        'Damage',
  theft:         'Theft',
  maintenance:   'Maintenance',
  other:         'Other',
}

watch(() => form.rental_id, (id) => {
  if (!id) return
  const rental = props.unpaid_rentals.find(r => r.id === parseInt(id))
  if (rental) {
    form.amount_cents = rental.balance_due_cents
    form.description  = `Unpaid balance written off — Rental #${rental.id}`
  }
})

const amountDisplay = computed({
  get: () => (form.amount_cents / 100).toFixed(2),
  set: (v) => { form.amount_cents = Math.round(parseFloat(v) * 100) || 0 },
})

function submit() {
  form.transform((data) => ({ loss: data })).post('/losses')
}

const inputBase = 'w-full rounded-[var(--radius-md)] border border-[color:var(--line)] bg-[color:var(--surface)] text-[color:var(--ink)] px-3 py-2 text-sm focus:outline-2 focus:outline-[color:var(--accent)]'
const labelCls  = 'block text-sm font-medium text-[color:var(--inkSoft)] mb-1'
</script>

<template>
  <PageShell title="New Loss" narrow>
    <div class="max-w-lg mx-auto w-full">
      <Link
        href="/losses"
        class="inline-flex items-center text-[color:var(--muted)] hover:text-[color:var(--ink)] transition-colors mb-4"
        aria-label="Back"
      >
        <Icon name="chev-l" :size="18" />
      </Link>

      <form
        id="loss-form"
        @submit.prevent="submit"
        class="rounded-[var(--radius-lg)] border border-[color:var(--line)] bg-[color:var(--surface)] p-6 space-y-5"
      >
        <div>
          <label :class="labelCls">Category *</label>
          <div class="grid grid-cols-3 gap-2">
            <label
              v-for="cat in categories"
              :key="cat"
              class="flex flex-col items-center justify-center p-3 rounded-[var(--radius-md)] border cursor-pointer text-center transition-colors"
              :style="form.category === cat
                ? { borderColor: 'var(--danger)', background: 'var(--dangerSoft)', color: 'var(--danger)' }
                : { borderColor: 'var(--line)', background: 'var(--surface)', color: 'var(--inkSoft)' }"
            >
              <input type="radio" :value="cat" v-model="form.category" class="sr-only" />
              <span class="text-xs font-medium">{{ categoryLabels[cat] }}</span>
            </label>
          </div>
        </div>

        <div v-if="form.category === 'unpaid_rental' && unpaid_rentals.length > 0">
          <label :class="labelCls">Link to Rental (optional)</label>
          <select v-model="form.rental_id" :class="inputBase">
            <option :value="null">— select to auto-fill amount —</option>
            <option v-for="r in unpaid_rentals" :key="r.id" :value="r.id">{{ r.label }}</option>
          </select>
        </div>

        <div v-if="['accident','damage','theft','maintenance'].includes(form.category)">
          <label :class="labelCls">Vehicle (optional)</label>
          <select v-model="form.vehicle_id" :class="inputBase">
            <option :value="null">— none —</option>
            <option v-for="v in vehicles" :key="v.id" :value="v.id">{{ v.label }}</option>
          </select>
        </div>

        <div>
          <label :class="labelCls">Amount ({{ setting.currency_code }}) *</label>
          <input v-model="amountDisplay" type="number" min="0.01" step="0.01" required :class="inputBase" />
          <p v-if="errors.amount_cents" class="text-xs text-[color:var(--danger)] mt-1">{{ errors.amount_cents[0] }}</p>
        </div>

        <div>
          <label :class="labelCls">Description *</label>
          <textarea v-model="form.description" rows="2" required placeholder="What happened?" :class="inputBase"></textarea>
          <p v-if="errors.description" class="text-xs text-[color:var(--danger)] mt-1">{{ errors.description[0] }}</p>
        </div>

        <div>
          <label :class="labelCls">Date *</label>
          <input v-model="form.occurred_on" type="date" required :class="inputBase" />
        </div>
      </form>

      <StickyActionBar>
        <UiButton
          type="submit"
          form="loss-form"
          variant="primary"
          :loading="form.processing"
        >
          {{ form.processing ? 'Saving…' : 'Record Loss' }}
        </UiButton>
      </StickyActionBar>
    </div>
  </PageShell>
</template>
